'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const CATEGORIES = [
  { id: 'ALL', label: 'All Categories' },
  { id: 'RAW_MATERIAL', label: 'Raw Material' },
  { id: 'CONSUMABLE', label: 'Consumable' },
  { id: 'SPARE_PART', label: 'Spare Part' },
  { id: 'TOOL', label: 'Tool' },
  { id: 'SAFETY_ITEM', label: 'Safety Item' },
  { id: 'PPE', label: 'PPE' },
  { id: 'FINISHED_GOOD', label: 'Finished Good' },
  { id: 'OTHER', label: 'Other' },
];

interface FiltersProps {
  search: string;
  onSearchChange: (v: string) => void;
  categoryFilter: string;
  onCategoryChange: (v: string) => void;
  statusFilter: 'ALL' | 'ACTIVE' | 'INACTIVE';
  onStatusChange: (v: 'ALL' | 'ACTIVE' | 'INACTIVE') => void;
  visibleCols: { hsnCode: boolean; barcode: boolean; lastMovement: boolean };
  onVisibleColsChange: (v: { hsnCode: boolean; barcode: boolean; lastMovement: boolean }) => void;
}

export default function ItemMasterFilters({
  search, onSearchChange, categoryFilter, onCategoryChange,
  statusFilter, onStatusChange, visibleCols, onVisibleColsChange,
}: FiltersProps) {
  const [colDropOpen, setColDropOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by item code, name, or HSN code…"
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm bg-card text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all"
          />
          {search && (
            <button onClick={() => onSearchChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <Icon name="XMarkIcon" size={14} />
            </button>
          )}
        </div>

        {/* Status toggle */}
        <div className="flex bg-muted p-1 rounded-lg shrink-0">
          {(['ALL', 'ACTIVE', 'INACTIVE'] as const).map((s) => (
            <button
              key={`status-fil-${s}`}
              onClick={() => onStatusChange(s)}
              className={`px-3 py-1.5 text-xs font-500 rounded-md transition-all ${
                statusFilter === s ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s === 'ALL' ? 'All' : s === 'ACTIVE' ? 'Active' : 'Inactive'}
            </button>
          ))}
        </div>

        {/* Column visibility */}
        <div className="relative shrink-0">
          <button
            onClick={() => setColDropOpen((p) => !p)}
            className="flex items-center gap-2 px-3 py-2.5 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors bg-card"
          >
            <Icon name="ViewColumnsIcon" size={15} />
            Columns
            <Icon name="ChevronDownIcon" size={12} className="text-muted-foreground" />
          </button>
          {colDropOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-xl shadow-dropdown z-20 fade-in p-3 space-y-2">
              {([
                { key: 'hsnCode', label: 'HSN Code' },
                { key: 'barcode', label: 'Barcode' },
                { key: 'lastMovement', label: 'Last Movement' },
              ] as const).map((col) => (
                <label key={`col-vis-${col.key}`} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleCols[col.key]}
                    onChange={(e) => onVisibleColsChange({ ...visibleCols, [col.key]: e.target.checked })}
                    className="w-4 h-4 rounded border-border accent-primary"
                  />
                  <span className="text-sm text-foreground">{col.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={`cat-chip-${cat.id}`}
            onClick={() => onCategoryChange(cat.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-all ${
              categoryFilter === cat.id
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>
    </div>
  );
}